import HubLeadCTA from "@/components/HubLeadCTA";
import NaatiClusterLinks from "@/components/NaatiClusterLinks";
// r24 Wave 2 — NAATI AU cities hub index (EN mirror)
import { createFileRoute, Link } from "@tanstack/react-router";
import { NAATI_AU_CITIES, type AuState } from "@/data/naati-au-cities";
import { canonicalAndHreflangEn, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const STATE_EN: Record<AuState, string> = {
  NSW: "New South Wales (NSW)", VIC: "Victoria (VIC)", QLD: "Queensland (QLD)",
  WA: "Western Australia (WA)", SA: "South Australia (SA)", TAS: "Tasmania (TAS)",
  ACT: "Australian Capital Territory (ACT)", NT: "Northern Territory (NT)",
};
const STATE_ORDER: AuState[] = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export const Route = createFileRoute("/en/translate/naati/au/")({
  head: () => {
    const url = `${SITE}/en/translate/naati/au`;
    const title = `NAATI Translation Shipped to 30 Australian Cities — Sydney, Melbourne, Brisbane, Perth`;
    const desc = `NAATI Certified translation delivered to ${NAATI_AU_CITIES.length} cities across 8 Australian states — one Bangkok price, DHL Express 3-5 days, hard copy + PDF, CPN verifiable in the NAATI Directory. Send your documents for a free check.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/naati/au/index", "en") }, { name: "description", content: ctaDesc(desc, "/en/translate/naati/au/index", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/translate/naati/au/index", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/translate/naati/au/index", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: canonicalAndHreflangEn("/en/translate/naati/au", "/translate/naati/au"),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "ItemList", inLanguage: "en",
          numberOfItems: NAATI_AU_CITIES.length,
          itemListElement: NAATI_AU_CITIES.map((c, i) => ({
            "@type": "ListItem", position: i + 1, name: `NAATI Translation ${c.nameEn}`,
            url: `${SITE}/en/translate/naati/au/${c.slug}`,
          })),
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
            { "@type": "ListItem", position: 2, name: "NAATI Translation", item: `${SITE}/en/translate/naati` },
            { "@type": "ListItem", position: 3, name: "Australian Cities", item: url },
          ],
        }) },
      ],
    };
  },
  component: AuHubEn,
});

function AuHubEn() {
  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <nav className="text-sm text-slate-500 mb-4" aria-label="Breadcrumb">
        <Link to="/en" className="hover:underline">Home</Link> ·{" "}
        <Link to="/en/translate/naati" className="hover:underline">NAATI Translation</Link> ·{" "}
        <span className="text-slate-700">Australian Cities</span>
      </nav>
      <h1 className="text-3xl md:text-4xl font-bold text-navy-deep mb-3">NAATI Translation Shipped to {NAATI_AU_CITIES.length} Australian Cities</h1>
      <p data-speakable="true" className="text-lg text-slate-700 mb-8 direct-answer">
        NYC ships NAATI-stamped translations to all 8 Australian states via DHL Express (3-5 business days). Pick a city to
        see the nearest BDM office, state-specific lodgement tips, and real casework we've handled there.
      </p>
      {STATE_ORDER.map((state) => {
        const cities = NAATI_AU_CITIES.filter((c) => c.state === state);
        if (cities.length === 0) return null;
        return (
          <section key={state} className="mb-10">
            <h2 className="text-2xl font-semibold text-navy-deep border-b border-gold pb-2 mb-4">
              {STATE_EN[state]} <span className="text-sm font-normal text-slate-500">({cities.length} cities)</span>
            </h2>
            <ul className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {cities.map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/en/translate/naati/au/$slug" params={{ slug: c.slug }}
                    className="block border rounded-lg p-3 hover:border-gold hover:bg-amber-50/50 transition"
                  >
                    <div className="font-medium text-navy-deep">{c.nameEn}</div>
                    <div className="text-xs text-slate-500 mt-1">{c.stateEn}{c.isCapital ? " · state capital" : ""}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      <NaatiClusterLinks />
    <HubLeadCTA locale="en" service="NAATI" />
      </main>
  );
}
